import { useCallback, useEffect, useState } from 'react';
import { getTokenExpiry, refreshAccessToken, WORKDOCS_APP_ID } from 'src/services/WorkDocsService';
import { STORAGE_CHANGE_EVENT, dispatchStorageChangeEvent } from 'src/hooks/useLocalStorage';
import { logger } from 'src/logger';

const STORAGE_KEY = 'workdocs_tokens';
const VERIFIER_KEY = 'workdocs_code_verifier';
const RETURN_PATH_KEY = 'workdocs_return_path';

const WORKDOCS_CALLBACK_PATH = '/workdocs/callback';

interface WorkDocsTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  // The user's WorkDocs home folder id (returned only by the /token exchange, not by
  // /token/refresh). This is the PARENT under which provisioned folder trees are created.
  rootFolderId?: string;
}

interface UseWorkDocsAuthReturn {
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // The user's WorkDocs home folder id, or null when unknown (e.g. tokens predate this
  // field, or only a refresh has happened). Consumers use it as the create-under parent.
  rootFolderId: string | null;
  initiateAuth: () => void;
  /**
   * On-demand silent token refresh: exchanges the stored refresh token for a fresh access token
   * WITHOUT the full OAuth redirect. Resolves to the new access token on success, or null if there
   * is nothing stored or the refresh failed (in which case tokens are cleared and `error` is set,
   * so callers should fall back to `initiateAuth`). Long-running consumers (e.g. WorkDocs folder
   * provisioning) call this when the access token lapses mid-operation to continue seamlessly.
   */
  refresh: () => Promise<string | null>;
  logout: () => void;
}

function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
    .slice(0, 43);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function getStoredTokens(): WorkDocsTokens | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Persist tokens and notify same-tab listeners. The WorkDocs callback runs on a
// separate route and writes tokens slightly after this hook has mounted, so the
// custom event is what lets an already-mounted hook pick them up without a reload.
function storeTokens(tokens: WorkDocsTokens): void {
  const serialized = JSON.stringify(tokens);
  localStorage.setItem(STORAGE_KEY, serialized);
  dispatchStorageChangeEvent(STORAGE_KEY, serialized);
}

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
  dispatchStorageChangeEvent(STORAGE_KEY, null);
}

// Coalesces concurrent silent refreshes into a single /token/refresh round-trip. The WorkDocs
// refresh token is SINGLE-USE (the backend rotates it on every exchange), and the Baseball Cards
// page mounts ~6 independent useWorkDocsAuth instances that each fire a mount refresh on an
// expired-token load. Without this guard they would each spend the same refresh token — one wins,
// the rest hit an already-rotated (dead) token and clearTokens(), wiping the good session the
// winner just wrote. This module-scope promise lets all callers await one exchange + one
// storeTokens write; state then propagates to every instance via STORAGE_CHANGE_EVENT.
let inFlightRefresh: Promise<string | null> | null = null;

// The shared refresh body: React-free (runs outside any component), touching only localStorage.
// Each hook instance applies its own React state after awaiting; other instances re-sync via the
// storage-change event that storeTokens/clearTokens dispatch.
async function performTokenRefresh(): Promise<string | null> {
  const stored = getStoredTokens();
  if (!stored) return null;

  try {
    const resp = await refreshAccessToken(stored.accessToken, stored.refreshToken);
    const expiresAt = getTokenExpiry(resp.accessToken);
    if (expiresAt === null) {
      throw new Error('Refreshed token missing expiryTimeInMS');
    }
    const tokens: WorkDocsTokens = {
      accessToken: resp.accessToken,
      refreshToken: resp.refreshToken,
      expiresAt,
      // CRITICAL: /token/refresh does NOT return user.RootFolderId (only the initial
      // /token exchange does), so carry the stored id forward. Dropping it here would
      // silently wipe the parent folder id after the first token expiry and make
      // folder provisioning impossible until the user fully re-authenticates.
      rootFolderId: stored.rootFolderId
    };
    storeTokens(tokens);
    return tokens.accessToken;
  } catch (err) {
    logger.warn('WorkDocs token refresh failed', { error: err });
    clearTokens();
    return null;
  }
}

// The current valid access token from storage, or null if absent/expired.
function readValidAccessToken(): string | null {
  const stored = getStoredTokens();
  return stored && Date.now() < stored.expiresAt ? stored.accessToken : null;
}

// The user's WorkDocs home folder id from storage, or null. Unlike the access token
// this is not gated on expiry — the parent id is stable across a token refresh and is
// carried forward so it survives even when the access token has lapsed.
function readRootFolderId(): string | null {
  return getStoredTokens()?.rootFolderId ?? null;
}

export function useWorkDocsAuth(): UseWorkDocsAuthReturn {
  // Initialize synchronously from localStorage to avoid flash of "Connect" screen
  const [accessToken, setAccessToken] = useState<string | null>(readValidAccessToken);
  const [rootFolderId, setRootFolderId] = useState<string | null>(readRootFolderId);
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    const stored = getStoredTokens();
    // Only loading if we have expired tokens that need refresh
    return stored !== null && Date.now() >= stored.expiresAt;
  });
  const [error, setError] = useState<string | null>(null);

  // On-demand silent refresh: swap the stored refresh token for a fresh access token without the
  // OAuth redirect. Returns the new access token, or null (and clears tokens + sets error) on
  // failure. Used both by the mount effect below AND by long-running consumers that hit expiry
  // mid-operation (e.g. WorkDocs folder provisioning), which is why it returns the token — callers
  // need the value synchronously and cannot wait for the `accessToken` state to re-render.
  const refresh = useCallback(async (): Promise<string | null> => {
    // Snapshot whether there was anything to refresh BEFORE awaiting, so a null result is
    // interpreted correctly: "had tokens but got null" is a real failure (surface reconnect),
    // whereas "nothing stored" is a no-op (return null silently, matching the original behavior).
    const hadTokens = getStoredTokens() !== null;

    // Coalesce concurrent refreshes (see performTokenRefresh): the first caller starts the shared
    // exchange; everyone else awaits the same promise. Reset the slot once settled so the NEXT
    // expiry can refresh again.
    if (!inFlightRefresh) {
      inFlightRefresh = performTokenRefresh();
      void inFlightRefresh.finally(() => {
        inFlightRefresh = null;
      });
    }
    const token = await inFlightRefresh;

    // Apply the outcome to THIS instance's state. The winner set localStorage; read it back for the
    // fresh accessToken/rootFolderId. Other instances also get resynced by the storage-change event,
    // but setting state here makes the caller's own render immediate.
    if (token) {
      const stored = getStoredTokens();
      setAccessToken(stored?.accessToken ?? token);
      setRootFolderId(stored?.rootFolderId ?? null);
      // Clear any stale "Session expired" from a prior failed refresh so a now-authenticated UI
      // does not keep showing the reconnect message.
      setError(null);
    } else if (hadTokens) {
      // We had a token but the refresh produced none (refresh token dead) — surface reconnect.
      setAccessToken(null);
      setError('Session expired — please reconnect WorkDocs');
    }
    return token;
  }, []);

  // On mount: if tokens exist but are expired, try a silent refresh once.
  useEffect(() => {
    const stored = getStoredTokens();
    if (!stored || Date.now() < stored.expiresAt) return;
    void refresh().finally(() => setIsLoading(false));
  }, [refresh]);

  // Re-sync from storage when the token changes underneath us. The WorkDocs OAuth
  // callback runs on its own route and writes the token slightly AFTER this hook
  // has mounted (a ~2s exchange round-trip), so the synchronous init above misses
  // it. Listening for the same-tab custom event (and the cross-tab native event)
  // lets the button unlock the moment the token lands, without a page reload.
  useEffect(() => {
    const resync = (): void => {
      setAccessToken((current) => {
        const next = readValidAccessToken();
        return current === next ? current : next;
      });
      setRootFolderId((current) => {
        const next = readRootFolderId();
        return current === next ? current : next;
      });
    };

    const handleCustomChange = (e: Event): void => {
      if ((e as CustomEvent<{ key: string }>).detail?.key === STORAGE_KEY) resync();
    };
    const handleNativeChange = (e: StorageEvent): void => {
      if (e.key === STORAGE_KEY) resync();
    };

    window.addEventListener(STORAGE_CHANGE_EVENT, handleCustomChange);
    window.addEventListener('storage', handleNativeChange);
    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleCustomChange);
      window.removeEventListener('storage', handleNativeChange);
    };
  }, []);

  // Live expiry timer: fires exactly when the current token lapses so
  // isAuthenticated (= !!accessToken) flips to false without a reload. The
  // deadline is read from the stored token (single source of truth); the effect
  // re-arms whenever accessToken changes (fresh auth / logout) and clears on unmount.
  useEffect(() => {
    if (!accessToken) return;
    const stored = getStoredTokens();
    if (!stored) return;
    const ms = stored.expiresAt - Date.now();
    if (ms <= 0) {
      setAccessToken(null);
      return;
    }
    const id = setTimeout(() => setAccessToken(null), ms);
    return () => clearTimeout(id);
  }, [accessToken]);

  const initiateAuth = useCallback(async () => {
    try {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store PKCE verifier and return path for the callback handler
      sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem('workdocs_pending', 'true');
      sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname);

      // Redirect to WorkDocs login — callback goes to the registered path
      const redirectUrl = `${window.location.origin}${WORKDOCS_CALLBACK_PATH}`;
      const authUrl = `https://amazon.awsapps.com/workdocs/loginv2/index.html#/checkAuth?sitename=amazon&code_challenge=${codeChallenge}&application_id=${WORKDOCS_APP_ID}&app_redirect=${encodeURIComponent(
        redirectUrl
      )}`;

      window.location.href = authUrl;
    } catch (err) {
      logger.error('Failed to initiate WorkDocs auth:', err as Error);
      setError('Failed to start authentication. Please try again.');
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setAccessToken(null);
    setRootFolderId(null);
  }, []);

  return {
    accessToken,
    isAuthenticated: !!accessToken,
    isLoading,
    error,
    rootFolderId,
    initiateAuth,
    refresh,
    logout
  };
}
